import { Button, Divider, FlexLayout, H4 } from '@deity/falcon-ui';
import { CircularProgress } from '@material-ui/core';
import { Add, Close } from '@mui/icons-material';
import EditMappingFormFields from 'components/Mappings/EditMappingFormFields';
import { Modal } from 'components/index';
import { Form, Formik } from 'formik';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { GetPackageVersionContentInfoDocument, useUpdateMappingMutation } from 'services/graphql/generated/hooks';
import type { GetMappingQuery } from 'services/graphql/generated/operations';
import * as Yup from 'yup';

interface EditMappingModalProps {
  isOpen?: boolean;
  versionUuid: string;
  editMapping?: GetMappingQuery;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
  id: string;
}

export interface EditMappingInput {
  name: string;
  description: string;
}

const EditMappingModal: FC<EditMappingModalProps> = ({
  isOpen,
  setOpenState,
  id,
  onPackageCreated,
  editMapping,
  versionUuid
}) => {
  const modalRef = useRef<any | null>(null);
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  };

  const [update, { data: updateData, loading: updateLoading }] = useUpdateMappingMutation(refetchQueries);

  const MappingInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(50, 'Too long')
  });

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (updateData) {
      onPackageCreated();
      modalRef.current?.close();
    }
  }, [updateData]);

  const onUpdate = (input: EditMappingInput) => {
    if (!input.description) {
      input.description = '';
    }

    return updateMapping(input);
  };

  const updateMapping = (input: EditMappingInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editMapping?.getMapping.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const initialValues: EditMappingInput = {
    name: editMapping?.getMapping.name ?? '',
    description: editMapping?.getMapping.description ?? ''
  };

  return (
    <Modal
      ref={modalRef}
      onOpenChange={state => {
        if (state !== isOpen) {
          setOpenState(state);
        }
      }}
      id={id}
    >
      <FlexLayout height="full" bg="white" borderRadius="small" flexDirection="column">
        <FlexLayout p="lg" justifyContent="space-between" alignItems="center">
          <H4 color="primaryText" fontSize="xxl" style={{ fontWeight: 'normal' }}>
            Edit Mapping
          </H4>
          <Button variant="transparent" color="secondary" onClick={() => modalRef.current?.close()}>
            <Close />
          </Button>
        </FlexLayout>
        <Divider />
        <Formik
          validationSchema={MappingInputSchema}
          initialValues={initialValues}
          validateOnMount
          validateOnBlur
          onSubmit={values => onUpdate(values)}
        >
          {({ errors, touched, values, setFieldValue, setFieldTouched, isValid }) => (
            <Form>
              <FlexLayout
                height="full"
                p="lg"
                flexDirection="column"
                justifyContent="space-between"
                style={{ flexGrow: 1 }}
              >
                <EditMappingFormFields
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
                <FlexLayout ml="auto" alignItems="center">
                  {!updateLoading && (
                    <Button variant="transparent" mr="xs" onClick={() => modalRef.current?.close()}>
                      Cancel
                    </Button>
                  )}
                  <Button disabled={updateLoading || !isValid} type="submit">
                    {updateLoading ? (
                      <CircularProgress
                        style={{ marginRight: '8px', color: '#BDC0D0', height: '24px', width: '24px' }}
                      />
                    ) : (
                      <Add style={{ marginRight: '4px' }} />
                    )}
                    {`${updateLoading ? 'Updating' : 'Update'} Mapping`}
                  </Button>
                </FlexLayout>
              </FlexLayout>
            </Form>
          )}
        </Formik>
      </FlexLayout>
    </Modal>
  );
};

export default EditMappingModal;
